/*
 * BEEST cart drawer (sections/beest-cart-drawer.liquid).
 *
 * - Liquid renders the drawer; every refresh re-renders it through the Section Rendering API.
 * - Any cart change made by other scripts (theme product form, beest-pdp.js, presell and
 *   Replo buy boxes) is picked up by wrapping fetch and XMLHttpRequest; adds open the drawer.
 * - Free gifts: the offers JSON (data-bcart-config) says which gift variants belong in the
 *   cart; syncGifts() adds and removes them so the cart always matches the offers.
 * - window.upcartOpenCart / upcartRefreshCart point here when UpCart is not on the page,
 *   so pages written for UpCart keep working.
 * - UpCart mode: when the UpCart embed is on, UpCart stays the drawer. This element then only
 *   keeps the gifts in sync and copies the bar, gift list, entries row and subtotal from the
 *   section's hidden [data-bcart-upcart] pieces into UpCart (UpcartEmbed below).
 */
(() => {
  if (customElements.get('beest-cart-drawer')) return;

  const nativeFetch = window.fetch.bind(window);
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const CART_ADD = /\/cart\/add(\.js|\.json)?(\?|$)/;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  const CELEBRATED_KEY = 'bcart-tiers-celebrated';
  const CONFETTI_DELAY_MS = 350;
  const CONFETTI_MS = 2400;
  const reduceMotion = () => !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
  const widthFor = (fill) => `${Math.max(0, Math.min(100, fill)).toFixed(2)}%`;
  // The drawer's own bar (the section also holds copies for UpCart further down).
  const BAR = ':scope > .bcart__panel > [data-bcart-inner] > [data-bcart-progress]';

  // While an app embed still owns the cart, stay out of its way: UpCart keeps the drawer,
  // BOGOS keeps the gifts. Turning the embed off in the theme hands the job over.
  const upcartOn = () => typeof window.upcartSettings !== 'undefined';
  const bogosOn = () => !!document.getElementById('secomapp_freegifts_version');

  const pathOf = (input) => {
    try {
      const raw = typeof input === 'string' ? input : (input && input.url) || String(input || '');
      return new URL(raw, location.href).pathname;
    } catch (e) {
      return '';
    }
  };

  class BeestCartDrawer extends HTMLElement {
    connectedCallback() {
      this.root = this.dataset.root || '/';
      this.sectionId = this.dataset.sectionId;
      this.openOnAdd = this.dataset.openOnAdd !== 'false';
      this.onCartPage = this.dataset.cartPage === 'true';
      this.queue = Promise.resolve();
      this.pending = null;
      this.inFlight = 0;
      this.lastFocus = null;
      this.pendingTier = null;
      this.celebrated = this.readCelebrated();
      const bar = this.querySelector(BAR);
      this.shownFill = bar ? Number(bar.dataset.fill) || 0 : 0;

      this.manageGifts = !bogosOn();
      if (!this.manageGifts) console.info('[BeestCart] BOGOS is on for this theme, so it keeps handling free gifts.');

      if (upcartOn()) {
        this.mode = 'upcart';
        this.embed = new UpcartEmbed(this);
        this.installCartHooks();
        // The pieces rendered with the page are current: show them at once. A page that loads
        // with an empty cart needs no request at all; otherwise the gifts are checked quietly.
        this.embed.update(this.dataset.itemCount === '0' ? null : document);
        if (this.dataset.itemCount !== '0') this.refresh({ initial: true });
        return;
      }

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('submit', this.onSubmit.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));

      window.BeestCart = {
        open: () => this.open(),
        close: () => this.close(),
        refresh: (opts) => this.refresh(opts),
        element: this,
      };
      this.installHooks();
      this.refresh({ initial: true });
    }

    get config() {
      try {
        return JSON.parse(this.querySelector('[data-bcart-config]').textContent);
      } catch (e) {
        return { offers: [], managed: [] };
      }
    }

    /* ---------- open / close ---------- */

    open() {
      if (this.classList.contains('is-open')) return;
      this.lastFocus = document.activeElement;
      this.hidden = false;
      void this.offsetWidth; // start the slide-in from the closed position
      this.classList.add('is-open');
      document.documentElement.classList.add('bcart-locked');
      const panel = this.querySelector('.bcart__panel');
      if (panel) panel.focus({ preventScroll: true });
      this.playFill();
      this.flushCelebration();
      document.dispatchEvent(new CustomEvent('beest:cart-opened'));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('bcart-locked');
      const panel = this.querySelector('.bcart__panel');
      const done = () => { if (!this.classList.contains('is-open')) this.hidden = true; };
      if (panel && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        panel.addEventListener('transitionend', done, { once: true });
        setTimeout(done, 400);
      } else {
        done();
      }
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('beest:cart-closed'));
    }

    /* ---------- events ---------- */

    onClick(event) {
      const t = event.target;
      if (t.closest('[data-bcart-close]')) { event.preventDefault(); this.close(); return; }

      const step = t.closest('[data-bcart-step]');
      if (step) {
        const line = step.closest('[data-bcart-line]');
        const qty = Math.max(0, Number(line.dataset.quantity) + Number(step.dataset.bcartStep));
        this.changeLine(line, qty);
        return;
      }

      if (t.closest('[data-bcart-clear]')) { this.mutate('cart/clear.js', {}); return; }

      const removeCode = t.closest('[data-bcart-remove-code]');
      if (removeCode) { this.removeCode(removeCode.dataset.bcartRemoveCode); return; }

      const checkout = t.closest('[data-bcart-checkout]');
      if (checkout && this.inFlight > 0) {
        // Let the gift sync finish so checkout gets the right lines.
        event.preventDefault();
        checkout.setAttribute('aria-busy', 'true');
        this.queue.then(() => { location.href = checkout.href; });
      }
    }

    onChange(event) {
      const input = event.target.closest('[data-bcart-qty]');
      if (!input) return;
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      this.changeLine(input.closest('[data-bcart-line]'), qty);
    }

    onSubmit(event) {
      const form = event.target.closest('[data-bcart-promo-form]');
      if (!form) return;
      event.preventDefault();
      const code = (form.elements.code.value || '').trim();
      if (code) this.applyCode(code, form);
    }

    onKeydown(event) {
      if (event.key === 'Escape') { this.close(); return; }
      if (event.key !== 'Tab' || !this.classList.contains('is-open')) return;
      const items = [...this.querySelectorAll('.bcart__panel ' + FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    /* ---------- cart requests ---------- */

    async request(path, body) {
      const res = await nativeFetch(this.root + path + (body ? '?bcart=1' : ''), {
        method: body ? 'POST' : 'GET',
        headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
      return data;
    }

    getCart() {
      return this.request('cart.js');
    }

    changeLine(line, quantity) {
      if (!line) return;
      line.setAttribute('aria-busy', 'true');
      this.mutate('cart/change.js', { id: line.dataset.lineKey, quantity });
    }

    async mutate(path, body) {
      this.setBusy(true);
      try {
        await this.request(path, body);
        this.showError('');
      } catch (err) {
        this.showError(err.message);
      }
      return this.refresh();
    }

    async applyCode(code, form) {
      const button = form.querySelector('button');
      if (button) button.setAttribute('aria-busy', 'true');
      let message = '';
      let ok = false;
      try {
        const cart = await this.getCart();
        const codes = (cart.discount_codes || []).map((d) => d.code);
        if (codes.some((c) => c.toLowerCase() === code.toLowerCase())) {
          message = 'That code is already applied.';
        } else {
          const updated = await this.request('cart/update.js', { discount: [...codes, code].join(',') });
          const entry = (updated.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
          if (entry && entry.applicable) {
            ok = true;
            message = 'Code applied.';
          } else {
            message = "That code doesn't work for this cart.";
            await this.request('cart/update.js', { discount: codes.join(',') });
          }
        }
      } catch (err) {
        message = err.message;
      }
      await this.refresh();
      const msg = this.querySelector('[data-bcart-promo-msg]');
      const details = this.querySelector('[data-bcart-promo]');
      if (details) details.open = true;
      if (msg) {
        msg.textContent = message;
        msg.classList.toggle('is-ok', ok);
        msg.hidden = !message;
      }
    }

    async removeCode(code) {
      try {
        const cart = await this.getCart();
        const codes = (cart.discount_codes || []).map((d) => d.code).filter((c) => c !== code);
        await this.request('cart/update.js', { discount: codes.join(',') });
      } catch (err) {
        this.showError(err.message);
      }
      await this.refresh();
      const details = this.querySelector('[data-bcart-promo]');
      if (details) details.open = true;
    }

    /* ---------- refresh pipeline ---------- */

    // Calls made while a refresh is waiting merge into it, so a burst of cart events
    // ends in one sync and one render.
    refresh(opts = {}) {
      if (this.pending) {
        this.pending.open = this.pending.open || !!opts.open;
        return this.pending.promise;
      }
      const job = { open: !!opts.open, initial: !!opts.initial };
      this.pending = job;
      this.inFlight += 1;
      job.promise = this.queue.then(() => new Promise((resolve) => setTimeout(resolve, 30))).then(async () => {
        this.pending = null;
        try {
          await this.run(job);
        } catch (err) {
          console.error('[BeestCart]', err);
        } finally {
          this.inFlight -= 1;
          this.setBusy(false);
        }
      });
      this.queue = job.promise;
      return job.promise;
    }

    async run(job) {
      if (this.mode === 'upcart') return this.runUpcart(job);
      let cart = await this.getCart();
      let changed = this.manageGifts ? await this.syncGifts(cart) : false;
      if (!job.initial || changed) {
        await this.render();
      }
      // Leftover BOGOS clones that no offer manages.
      const stale = this.manageGifts ? [...this.querySelectorAll('[data-bcart-stale]')].map((el) => el.dataset.lineKey) : [];
      if (stale.length) {
        const updates = {};
        stale.forEach((key) => { updates[key] = 0; });
        await this.request('cart/update.js', { updates });
        changed = true;
        await this.render();
      }
      if (changed) cart = await this.getCart();
      this.updateCount(cart);
      this.syncProgress(cart);
      document.dispatchEvent(new CustomEvent('beest:cart-updated', { detail: { cart } }));
      if (job.open && !this.onCartPage) this.open();
      // The cart page is rendered by the theme, so show gift changes with one reload.
      if (changed && this.onCartPage) {
        let last = 0;
        try { last = Number(sessionStorage.getItem('bcart-reload')) || 0; } catch (e) { /* storage blocked */ }
        if (Date.now() - last > 5000) {
          try { sessionStorage.setItem('bcart-reload', String(Date.now())); } catch (e) { /* storage blocked */ }
          location.reload();
        }
      }
    }

    // UpCart mode. Everything this element adds to UpCart comes from one section render, and that
    // render carries the cart too ([data-bcart-cart]). So a change costs one request, which is not
    // queued behind the /cart.js reads UpCart and other apps make at the same moment (Shopify
    // answers those one at a time). Gift changes ask for the section in the same request.
    async runUpcart(job) {
      this.lastRunAt = Date.now();
      const managed = new Set(this.config.managed || []);
      const hasPaid = (c) => c.items.some((item) => !managed.has(item.variant_id) && !isBogosGift(item));

      // UpCart's own change.js already returned the cart: clear at once when nothing paid is left.
      const hint = this.cartHint;
      this.cartHint = null;
      const hinted = hint && Date.now() - hint.at < 3000 ? await hint.promise.catch(() => null) : null;
      if (hinted && Array.isArray(hinted.items) && !hasPaid(hinted)) this.embed.update(null);

      let doc = job.initial ? document : await this.fetchSection();
      let cart = readCart(doc) || (await this.getCart());

      if (this.manageGifts) {
        const plan = this.planGifts(cart);
        const stale = doc ? [...doc.querySelectorAll('[data-bcart-stale]')].map((el) => el.dataset.lineKey) : [];
        if (plan || stale.length) {
          const updates = plan ? { ...plan.updates } : {};
          stale.forEach((key) => { updates[key] = 0; });
          let data = null;
          try {
            if (Object.keys(updates).length) data = await this.request('cart/update.js', { updates, sections: this.sectionId });
            if (plan && plan.adds.length) data = await this.request('cart/add.js', { items: plan.adds, sections: this.sectionId });
          } catch (err) {
            console.warn('[BeestCart] gift sync', err);
          }
          this.selfRefreshAt = Date.now();
          refreshUpcart();
          const html = data && data.sections && data.sections[this.sectionId];
          doc = html ? new DOMParser().parseFromString(html, 'text/html') : await this.fetchSection();
          cart = readCart(doc) || (await this.getCart());
        }
      }

      this.embed.update(hasPaid(cart) ? doc : null);
      document.dispatchEvent(new CustomEvent('beest:cart-updated', { detail: { cart } }));
    }

    // Which gift lines to change so the cart matches the offers: { updates, adds }, or null.
    planGifts(cart) {
      const { offers = [], managed = [] } = this.config;
      if (!managed.length) return null;
      const managedSet = new Set(managed);
      const paid = cart.items.filter((item) => !managedSet.has(item.variant_id));

      const desired = new Map();
      for (const offer of offers) {
        const triggers = new Set(offer.triggers);
        const count = paid.filter((item) => triggers.has(item.product_id)).reduce((n, item) => n + item.quantity, 0);
        const min = Math.max(1, offer.min || 1);
        if (count < min) continue;
        const times = offer.multiply ? Math.floor(count / min) : 1;
        offer.gifts.forEach((gift) => {
          desired.set(gift.variant, (desired.get(gift.variant) || 0) + gift.qty * times);
        });
        if (offer.stop) break;
      }

      const updates = {};
      const kept = new Map();
      cart.items.forEach((item) => {
        if (!managedSet.has(item.variant_id)) return;
        const want = desired.get(item.variant_id) || 0;
        const already = kept.get(item.variant_id) || 0;
        const keep = Math.max(0, Math.min(item.quantity, want - already));
        if (keep !== item.quantity) updates[item.key] = keep;
        kept.set(item.variant_id, already + keep);
      });
      const adds = [];
      desired.forEach((qty, variant) => {
        const have = kept.get(variant) || 0;
        if (qty > have) adds.push({ id: variant, quantity: qty - have });
      });

      return Object.keys(updates).length || adds.length ? { updates, adds } : null;
    }

    async syncGifts(cart) {
      const plan = this.planGifts(cart);
      if (!plan) return false;
      try {
        if (Object.keys(plan.updates).length) await this.request('cart/update.js', { updates: plan.updates });
        if (plan.adds.length) await this.request('cart/add.js', { items: plan.adds });
      } catch (err) {
        console.warn('[BeestCart] gift sync', err);
      }
      return true;
    }

    async fetchSection() {
      const res = await nativeFetch(`${this.root}?sections=${encodeURIComponent(this.sectionId)}`, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`Section render failed (${res.status})`);
      const data = await res.json();
      const html = data[this.sectionId];
      return html ? new DOMParser().parseFromString(html, 'text/html') : null;
    }

    async render() {
      const doc = await this.fetchSection();
      if (!doc) return;
      const next = doc.querySelector('[data-bcart-inner]');
      const current = this.querySelector('[data-bcart-inner]');
      if (!next || !current) return;
      const promoOpen = !!(current.querySelector('[data-bcart-promo]') || {}).open;
      const scroll = (current.querySelector('.bcart__body') || {}).scrollTop || 0;
      const focusedLine = document.activeElement && document.activeElement.closest && document.activeElement.closest('[data-line-key]');
      const focusKey = focusedLine && this.contains(focusedLine) ? focusedLine.dataset.lineKey : null;
      const focusSelector = focusKey && document.activeElement.matches('[data-bcart-qty]') ? '[data-bcart-qty]' : focusKey ? '[data-bcart-step="1"]' : null;

      current.innerHTML = next.innerHTML;
      if (this.classList.contains('is-open')) this.playFill();

      const details = current.querySelector('[data-bcart-promo]');
      if (details && promoOpen) details.open = true;
      const body = current.querySelector('.bcart__body');
      if (body) body.scrollTop = scroll;
      if (focusKey && focusSelector) {
        const el = current.querySelector(`[data-line-key="${CSS.escape(focusKey)}"] ${focusSelector}`);
        if (el) el.focus({ preventScroll: true });
      }
      if (this.classList.contains('is-open') && !current.contains(document.activeElement)) {
        const panel = this.querySelector('.bcart__panel');
        if (panel) panel.focus({ preventScroll: true });
      }
    }

    updateCount(cart) {
      document.querySelectorAll('cart-count, [data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
        el.setAttribute('aria-label', `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`);
      });
      document.documentElement.classList.toggle('cart-has-items', cart.item_count > 0);
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
    }

    /* ---------- tier progress bar ---------- */

    // Same behaviour as the SharedSweeps bar that ran inside UpCart: the fill slides from
    // where the shopper last saw it, and confetti fires once per tier crossed in a visit.
    syncProgress(cart) {
      const bar = this.querySelector(BAR);
      if (!bar) {
        this.shownFill = 0;
        if (cart.item_count === 0) this.noteReached(0);
        return;
      }
      this.noteReached(Number(bar.dataset.reached) || 0);
      if (this.classList.contains('is-open')) {
        this.playFill();
        this.flushCelebration();
      }
    }

    // Liquid renders the fill at its final width. Put it back where it was, then let the
    // CSS transition run to the new width. Only while the drawer is on screen; open() calls
    // this again so an add from a closed drawer animates as it slides in.
    playFill() {
      const bar = this.querySelector(BAR);
      const fill = bar && bar.querySelector('[data-bcart-progress-fill]');
      if (!fill) return;
      const target = Number(bar.dataset.fill) || 0;
      if (target === this.shownFill || reduceMotion()) {
        this.shownFill = target;
        return;
      }
      fill.style.transition = 'none';
      fill.style.width = widthFor(this.shownFill);
      void fill.offsetWidth;
      fill.style.transition = '';
      fill.style.width = widthFor(target);
      this.shownFill = target;
    }

    readCelebrated() {
      try {
        const v = sessionStorage.getItem(CELEBRATED_KEY);
        return v == null ? null : Number(v) || 0;
      } catch (e) {
        return null;
      }
    }

    writeCelebrated(n) {
      if (this.celebrated === n) return;
      this.celebrated = n;
      try { sessionStorage.setItem(CELEBRATED_KEY, String(n)); } catch (e) { /* storage blocked */ }
    }

    // The first count seen in a visit is the baseline (no confetti for a cart that was
    // already over a tier). Dropping below a tier re-arms it.
    noteReached(reached) {
      if (this.celebrated == null) {
        this.writeCelebrated(reached);
        this.pendingTier = null;
        return;
      }
      if (reached < this.celebrated) this.writeCelebrated(reached);
      this.pendingTier = reached > this.celebrated ? reached - 1 : null;
    }

    flushCelebration() {
      if (this.pendingTier == null || !this.classList.contains('is-open')) return;
      const bar = this.querySelector(BAR);
      if (!bar) return;
      const index = this.pendingTier;
      this.pendingTier = null;
      this.writeCelebrated(index + 1);
      if (bar.dataset.confetti === 'false' || reduceMotion()) return;
      setTimeout(() => {
        const current = this.querySelector(BAR);
        if (!current || !this.classList.contains('is-open')) return;
        // Burst from the tick of the step just reached.
        const mark = current.querySelectorAll('[data-bcart-mark]')[index] || current;
        const r = mark.getBoundingClientRect();
        const css = getComputedStyle(this);
        const colors = [css.getPropertyValue('--bcart-bar-from'), css.getPropertyValue('--bcart-bar-to'), css.getPropertyValue('--bcart-bar'), '#E9B44C']
          .map((c) => c.trim()).filter(Boolean);
        burstConfetti(r.left + r.width / 2, r.top + r.height / 2, index, colors);
      }, CONFETTI_DELAY_MS);
    }

    showError(message) {
      const el = this.querySelector('[data-bcart-error]');
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
    }

    /* ---------- hooks into the rest of the page ---------- */

    // Any cart change on the page, whoever makes it, ends in a refresh (adds also open the drawer).
    installCartHooks() {
      const drawer = this;
      const afterCartRequest = (path) => {
        if (!CART_MUTATION.test(path)) return;
        drawer.refresh({ open: drawer.openOnAdd && CART_ADD.test(path) });
      };

      // change.js, update.js and clear.js answer with the whole cart; keep it for the next run.
      const keepCart = (path, read) => {
        if (/\/cart\/(change|update|clear)/.test(path)) drawer.cartHint = { at: Date.now(), promise: read() };
      };

      window.fetch = function (input, init) {
        const path = pathOf(input);
        const promise = nativeFetch(input, init);
        if (CART_MUTATION.test(path)) {
          promise.then((res) => {
            if (!res || !res.ok) return;
            keepCart(path, () => res.clone().json());
            afterCartRequest(path);
          }).catch(() => {});
        }
        return promise;
      };

      const open = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this.__bcartPath = pathOf(url);
        return open.call(this, method, url, ...rest);
      };
      const send = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function (...args) {
        if (CART_MUTATION.test(this.__bcartPath || '')) {
          this.addEventListener('load', () => {
            if (this.status < 200 || this.status >= 300) return;
            keepCart(this.__bcartPath, () => Promise.resolve().then(() => JSON.parse(this.responseText)));
            afterCartRequest(this.__bcartPath);
          });
        }
        return send.apply(this, args);
      };

      // Scripts that kept their own reference to fetch (UpCart can) still show up here.
      if (this.mode === 'upcart' && 'PerformanceObserver' in window) {
        try {
          new PerformanceObserver((list) => {
            const hit = list.getEntries().some((e) => !e.name.includes('bcart=1') && CART_MUTATION.test(pathOf(e.name)));
            if (hit) drawer.refresh();
          }).observe({ type: 'resource' });
        } catch (e) { /* not supported */ }
      }
    }

    installHooks() {
      const drawer = this;
      this.installCartHooks();

      // Cart icons and /cart links open the drawer instead of the cart page.
      document.addEventListener('click', (event) => {
        if (drawer.onCartPage || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
        const trigger = event.target.closest('a[href], [aria-controls="CartDrawer"], [data-open-cart]');
        if (!trigger || drawer.contains(trigger)) return;
        let isCartLink = false;
        if (trigger.hasAttribute('href')) {
          try {
            const url = new URL(trigger.getAttribute('href'), location.href);
            isCartLink = url.host === location.host && /^\/cart\/?$/.test(url.pathname);
          } catch (e) { /* not a URL */ }
        }
        if (!isCartLink && !trigger.matches('[aria-controls="CartDrawer"], [data-open-cart]')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        drawer.open();
      }, true);

      // The theme's own drawer opens ours instead.
      customElements.whenDefined('cart-drawer').then(() => {
        const ThemeDrawer = customElements.get('cart-drawer');
        if (!ThemeDrawer || ThemeDrawer.prototype.__bcartPatched) return;
        ThemeDrawer.prototype.__bcartPatched = true;
        ThemeDrawer.prototype.show = function () { drawer.refresh({ open: true }); };
      });

      // Pages written for UpCart call these.
      if (typeof window.upcartOpenCart !== 'function') window.upcartOpenCart = () => drawer.refresh({ open: true });
      if (typeof window.upcartRefreshCart !== 'function') window.upcartRefreshCart = () => drawer.refresh();
      if (typeof window.upcartCloseCart !== 'function') window.upcartCloseCart = () => drawer.close();

      // Theme editor: show the drawer while its section is selected.
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail && event.detail.sectionId === drawer.sectionId) drawer.open();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail && event.detail.sectionId === drawer.sectionId) drawer.close();
      });
    }
  }

  /* ---------- UpCart mode ---------- */

  // UpCart's public drawer markup (the SharedSweeps UpCart script relies on the same hooks).
  const UC = {
    host: 'upCart',
    header: '.upcart-public-header',
    item: 'li.upcart-public-cart-item',
    link: 'a[href*="variant="]',
    checkout: '.upcart-checkout-button-container',
  };

  // BOGOS gifts are $0 "Free ..." products with a handle ending in -sca_clone_freegift. The
  // section marks them (and offer gifts) as "gift"; a cart from UpCart's change.js has the handle.
  const BOGOS_HANDLE = 'sca_clone_freegift';
  const isBogosGift = (item) => item.gift === true
    || (item.final_price === 0 && String(item.handle || item.url || '').includes(BOGOS_HANDLE));

  // The cart summary a section render carries ([data-bcart-cart]), or null.
  const readCart = (doc) => {
    try {
      const data = JSON.parse(doc.querySelector('[data-bcart-cart]').textContent);
      return Array.isArray(data.items) ? data : null;
    } catch (e) {
      return null;
    }
  };

  // Ask UpCart to re-read the cart after the gift sync changed it.
  function refreshUpcart() {
    if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
  }

  class UpcartEmbed {
    constructor(drawer) {
      this.drawer = drawer;
      this.parts = null;
      this.root = null;
      this.timer = null;
      this.managed = new Set((drawer.config.managed || []).map(String));
      // Variants the last section render marked as gifts (offer gifts and BOGOS gifts).
      this.gifts = new Set();
      this.findRoot();
    }

    findRoot() {
      const started = Date.now();
      const check = () => {
        const host = document.getElementById(UC.host);
        const root = host && (host.shadowRoot || (host.querySelector('#UpcartPopup') ? host : null));
        if (root) {
          this.root = root;
          const strike = this.drawer.style.getPropertyValue('--bcart-strike');
          if (strike) host.style.setProperty('--bcart-strike', strike);
          this.ensureStyles();
          new MutationObserver(() => this.schedule()).observe(root, { childList: true, subtree: true });
          // Changes made inside UpCart (qty, remove) sync the gifts too. Skip the event UpCart
          // fires for a refresh this element just asked for, or for a change already handled.
          if (typeof window.upcartSubscribeCartUpdated === 'function') {
            window.upcartSubscribeCartUpdated(() => {
              const d = this.drawer;
              const recent = Date.now() - Math.max(d.selfRefreshAt || 0, d.lastRunAt || 0) < 1200;
              if (!recent || d.pending) d.refresh();
            });
          }
          this.schedule();
        } else if (Date.now() - started < 60000) {
          setTimeout(check, 250);
        }
      };
      check();
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.apply(), 60);
    }

    // Each UpCart cart picks its version: a data-beest-cart="giveaway|evergreen" marker in one of
    // its custom HTML fields, or "--beest-cart: evergreen;" on .upcart-public-header in its custom
    // CSS. No marker: Giveaway.
    get version() {
      const marker = this.root && this.root.querySelector('[data-beest-cart]');
      const header = this.root && this.root.querySelector(UC.header);
      const css = header ? getComputedStyle(header).getPropertyValue('--beest-cart').trim().replace(/["']/g, '') : '';
      const value = (marker && marker.dataset.beestCart) || css;
      return value === 'evergreen' ? 'evergreen' : 'giveaway';
    }

    update(doc) {
      const box = doc && doc.querySelector('[data-bcart-upcart]');
      const part = (name) => {
        const el = box && box.querySelector(`[data-bcart-part="${name}"]`);
        return el ? el.innerHTML.trim() : '';
      };
      const cart = doc && readCart(doc);
      if (cart) this.gifts = new Set(cart.items.filter((item) => item.gift).map((item) => String(item.variant_id)));
      this.parts = {
        'bar-giveaway': part('bar-giveaway'),
        'bar-evergreen': part('bar-evergreen'),
        gifts: part('gifts'),
        entries: part('entries'),
        subtotal: part('subtotal'),
      };
      this.apply();
    }

    apply() {
      const root = this.root;
      if (!root || !this.parts) return;
      this.ensureStyles();
      const version = this.version;

      this.place('bar', this.parts[`bar-${version}`], root.querySelector(UC.header), 'afterend');

      // Gift lines show in the gift list, so UpCart's own tiles for them are hidden.
      const items = [...root.querySelectorAll(UC.item)];
      items.forEach((li) => {
        const link = li.querySelector(UC.link);
        const href = (link && link.getAttribute('href')) || '';
        let id = null;
        try { id = link && new URL(href, location.origin).searchParams.get('variant'); } catch (e) { /* no link */ }
        const isGift = href.includes(BOGOS_HANDLE) || (!!id && (this.managed.has(id) || this.gifts.has(id)));
        if (li.hasAttribute('data-bcart-hide') !== isGift) li.toggleAttribute('data-bcart-hide', isGift);
      });
      const list = items.length ? items[items.length - 1].parentElement : null;
      this.place('gifts', this.parts.gifts, list, 'afterend');

      const checkout = root.querySelector(UC.checkout);
      const wrap = checkout && checkout.parentElement;
      const anchor = wrap && !wrap.classList.contains('upcart-footer') ? wrap : checkout;
      const totals = (version === 'giveaway' ? this.parts.entries : '') + this.parts.subtotal;
      this.place('totals', totals, anchor, 'beforebegin');
    }

    // Keep one copy of a piece next to its anchor; only touch the DOM when something changed,
    // so UpCart's own re-renders and this observer don't chase each other.
    place(key, html, anchor, where) {
      let node = this.root.querySelector(`[data-bcart-embed="${key}"]`);
      if (!html || !anchor) {
        if (node) node.remove();
        return;
      }
      if (!node) {
        node = document.createElement('div');
        node.className = 'bcart-embed';
        node.dataset.bcartEmbed = key;
        node.setAttribute('style', this.drawer.getAttribute('style') || '');
      }
      if (node._html !== html) {
        node.innerHTML = html;
        node._html = html;
      }
      const sibling = where === 'afterend' ? anchor.nextElementSibling : anchor.previousElementSibling;
      if (sibling !== node) anchor.insertAdjacentElement(where, node);
    }

    // UpCart renders in a shadow root, so the stylesheet goes in there too.
    ensureStyles() {
      const root = this.root;
      if (!(root instanceof ShadowRoot) || root.querySelector('link[data-bcart-css]')) return;
      // UpCart's own tiles for gift lines (offer gifts and every BOGOS gift), hidden by CSS from
      // the moment UpCart draws them.
      const style = document.createElement('style');
      style.dataset.bcartGiftCss = '';
      const hidden = [`${UC.item}:has(a[href*="${BOGOS_HANDLE}"])`, ...[...this.managed].map((id) => `${UC.item}:has(a[href*="variant=${id}"])`)];
      style.textContent = `${hidden.join(',')}{display:none !important}`;
      root.prepend(style);
      const source = document.querySelector('link[href*="beest-cart.css"]');
      if (!source) return;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = source.href;
      link.dataset.bcartCss = '';
      root.prepend(link);
    }
  }

  // Canvas confetti from the tier's segment, as in the SharedSweeps UpCart script.
  function burstConfetti(x, y, tierIndex, colors) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return;
    canvas.setAttribute('aria-hidden', 'true');
    canvas.width = Math.round(vw * dpr);
    canvas.height = Math.round(vh * dpr);
    canvas.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;pointer-events:none;z-index:2147483647;';
    document.body.appendChild(canvas);
    ctx.scale(dpr, dpr);
    const palette = colors.length ? colors : ['#000000'];
    const GRAVITY = 900;
    const DRAG = 2.6;
    const count = Math.min(160, 70 + tierIndex * 35);
    const parts = [];
    for (let i = 0; i < count; i++) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.7;
      const speed = 500 + Math.random() * 450;
      parts.push({
        x, y,
        vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
        rot: Math.random() * Math.PI * 2, vr: (Math.random() - 0.5) * 12,
        flip: Math.random() * Math.PI * 2, vf: 6 + Math.random() * 8,
        w: 6 + Math.random() * 5, h: 3 + Math.random() * 4,
        round: Math.random() < 0.3, color: palette[i % palette.length],
      });
    }
    let start = null;
    let last = null;
    const frame = (ts) => {
      if (start === null) start = last = ts;
      const dt = Math.min((ts - last) / 1000, 0.05);
      last = ts;
      const elapsed = ts - start;
      const fadeFrom = CONFETTI_MS * 0.65;
      ctx.clearRect(0, 0, vw, vh);
      ctx.globalAlpha = elapsed < fadeFrom ? 1 : Math.max(0, 1 - (elapsed - fadeFrom) / (CONFETTI_MS - fadeFrom));
      for (const p of parts) {
        p.vx -= DRAG * p.vx * dt;
        p.vy += (GRAVITY - DRAG * p.vy) * dt;
        p.x += (p.vx + Math.sin(p.flip) * 30) * dt;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;
        p.flip += p.vf * dt;
        if (p.y > vh + 20) continue;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.scale(1, Math.cos(p.flip));
        ctx.fillStyle = p.color;
        if (p.round) {
          ctx.beginPath();
          ctx.arc(0, 0, p.h * 0.75, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      }
      if (elapsed < CONFETTI_MS) requestAnimationFrame(frame);
      else canvas.remove();
    };
    requestAnimationFrame(frame);
  }

  customElements.define('beest-cart-drawer', BeestCartDrawer);
})();
